/**
 * Modal d'annulation enrichi avec sélection de raisons
 * Composant réutilisable pour MyReservations et OwnerReservations
 */

import React, { useState, useCallback } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Backdrop,
  Fade,
  Paper,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme, alpha } from '@mui/material/styles';
import CancellationReasonSelector from './CancellationReasonSelector';
import {
  Cancel as CancelIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Euro as EuroIcon
} from '@mui/icons-material';

const CancellationModalEnhanced = ({
  open,
  onClose,
  onConfirm,
  title,
  reservation,
  context, // 'renter_cancellation' | 'owner_cancellation'  
  userRole,
  loading = false,
  showRules = true,
  showReservationDetails = true
}) => {
  const { t } = useTranslation(['cancellationReasons', 'dashboard', 'common']);
  const theme = useTheme();
  
  // État pour la validation - doit utiliser useState pour déclencher re-render
  const [isReasonValid, setIsReasonValid] = useState(false);
  const [reasonData, setReasonData] = useState(null);
  
  // Callbacks hyper-stables
  const handleConfirm = React.useCallback(() => {
    if (isReasonValid && reasonData && onConfirm) {
      onConfirm({
        reason: reasonData.reason,
        analytics: reasonData.analytics,
        reservation
      });
    }
  }, [onConfirm, reservation, isReasonValid, reasonData]);

  const handleClose = React.useCallback(() => {
    if (!loading && onClose) {
      onClose();
    }
  }, [loading, onClose]);

  // Calculer les heures avant le début
  const getHoursBeforeStart = () => {
    if (!reservation?.start_date) return 0;
    const startDate = new Date(`${reservation.start_date}T00:00:00`);
    const now = new Date();
    return Math.round((startDate - now) / (1000 * 60 * 60));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: 650 },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 0
          }}
        >
          {/* Rendu conditionnel du contenu pour optimiser les performances */}
          {open && (
            <>
              {/* Header */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                  borderRadius: '12px 12px 0 0'
                }}
              >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.error.main,
                    color: 'white'
                  }}
                >
                  <CancelIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="error.main">
                    {t('modalTitlecancel')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('modalSubtitle')}
                  </Typography>
                </Box>
              </Stack>
              
              <IconButton onClick={handleClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Paper>

          <Box sx={{ p: 3 }}>
            {/* Alerte importante */}
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }} 
              icon={<WarningIcon />}
            >
              <Typography variant="body2" fontWeight={500}>
                {t('warningMessage')}
              </Typography>
            </Alert>

            {/* Informations réservation */}
            {showReservationDetails && reservation && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  mb: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                  {t('reservationInfo')}
                </Typography>
                
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <InfoIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reservationNumber')} 
                    </Typography>

                    <Typography variant="body2" color="text.primary" fontWeight="bold">
                      #{reservation.id?.slice(-8)}
                    </Typography>
                  </Stack>
                  
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TimeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {reservation.start_date} - {reservation.end_date}
                    </Typography>
                    <Typography variant="caption" color="warning.main" fontWeight={600}>
                      ({getHoursBeforeStart()}h {t('beforeStart')})
                    </Typography>
                  </Stack>
                  
                  {reservation.total_amount && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      💰
                      <Typography variant="body2" color="text.secondary">
                        {reservation.total_amount} - {reservation.currency || 'EUR'}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Sélecteur de raison */}
            <CancellationReasonSelector
              context={context}
              userRole={userRole}
              onChange={(data) => {
                console.log('🔧 [CancellationModal] Reason data updated:', data);
                setReasonData(data);
              }}
              onValidationChange={(valid) => {
                console.log('🔧 [CancellationModal] Validation changed:', valid);
                setIsReasonValid(valid);
              }}
              disabled={loading}
              variant="outlined"
            />

            {/* Règles d'annulation */}
            {showRules && (
              <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2, mb: 3 }}>
                <Typography variant="body2">
                  <strong>{t('rulesReminder')}</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {t('rulesDetails')}
                </Typography>
              </Alert>
            )}

            {/* Actions */}
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                fullWidth
                sx={{ 
                  py: 1.5,
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'grey.400',
                    backgroundColor: alpha(theme.palette.grey[500], 0.04)
                  }
                }}
              >
                {t('cancel')}
              </Button>
              
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={!isReasonValid || loading}
                color="error"
                fullWidth
                sx={{ 
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
                    boxShadow: `0 6px 16px ${alpha(theme.palette.error.main, 0.4)}`
                  },
                  '&:disabled': {
                    background: 'grey.300',
                    boxShadow: 'none'
                  }
                }}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CancelIcon />
                  )
                }
              >
                {loading 
                  ? t('processing')
                  : t('confirmCancellation')
                }
              </Button>
            </Stack>
          </Box>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

// 🎯 SOLUTION RADICALE: Comparateur personnalisé pour éviter tous les re-renders
const areEqual = (prevProps, nextProps) => {
  // OPTIMISATION : Ne comparer que open pour éviter les re-rendus inutiles
  // 1. Si le modal est fermé dans les deux états, pas besoin de re-render
  if (!prevProps.open && !nextProps.open) return true;
  
  // 2. Si l'état d'ouverture change, on doit re-render
  if (prevProps.open !== nextProps.open) return false;
  
  // 3. Si le loading change, on doit re-render
  if (prevProps.loading !== nextProps.loading) return false;
  
  // 4. Si la réservation change (par ID), on doit re-render
  const prevReservationId = prevProps.reservation?.id;
  const nextReservationId = nextProps.reservation?.id;
  if (prevReservationId !== nextReservationId) return false;
  
  // Ignorer tous les autres changements (userRole, etc.) qui peuvent causer des boucles
  return true;
};

export default React.memo(CancellationModalEnhanced, areEqual);
